import { supabase } from "../supabase";
import { formatSupabaseError } from "./estimates";
import { loadMaterialUsageForProject } from "./projectMaterialLedger";
import { loadLabourForProject } from "./projectLabourLedger";
import { getVisibleTimeLogs } from "./timeLogVisibility";

function wrapSupabaseError(error) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.cause = error;
  throw wrapped;
}

function normalizeBillingDate(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 10);
}

export function isDateWithinBillingPeriod(dateValue, startDate, endDate) {
  if (!startDate && !endDate) return true;

  const date = normalizeBillingDate(dateValue);
  if (!date) return false;
  if (startDate && date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
}

async function loadAllocatedUsageIds(usageIds = []) {
  if (!usageIds.length) {
    return new Set();
  }

  const { data, error } = await supabase
    .from("invoice_usage_allocations")
    .select("usage_id")
    .in("usage_id", usageIds);

  if (error) wrapSupabaseError(error);
  return new Set((data || []).map((row) => row.usage_id));
}

async function loadAllocatedTimeLogIds(timeLogIds = []) {
  if (!timeLogIds.length) {
    return new Set();
  }

  const { data, error } = await supabase
    .from("invoice_labour_allocations")
    .select("time_log_id")
    .in("time_log_id", timeLogIds);

  if (error) wrapSupabaseError(error);
  return new Set((data || []).map((row) => row.time_log_id));
}

export async function getUnallocatedMaterialUsage(
  projectId,
  { startDate = null, endDate = null, actor = null } = {},
) {
  const rows = await loadMaterialUsageForProject(projectId, { actor });
  const usageIds = rows.map((row) => row.usage_id).filter(Boolean);
  const allocated = await loadAllocatedUsageIds(usageIds);

  return rows.filter(
    (row) =>
      !allocated.has(row.usage_id) &&
      isDateWithinBillingPeriod(row.usage_date, startDate, endDate),
  );
}

export async function getUnallocatedTimeLogs(
  projectId,
  { startDate = null, endDate = null, actor = null } = {},
) {
  const rows = await loadLabourForProject(projectId, {
    select:
      "id, employee, supabase_user_id, date_iso, total_hours, net_work_seconds, hourly_rate, work_completed, approval_status, quote_line_snapshot_id",
    actor,
  });
  const timeLogIds = rows.map((row) => row.id).filter(Boolean);
  const allocated = await loadAllocatedTimeLogIds(timeLogIds);

  return getVisibleTimeLogs(rows).filter(
    (row) =>
      !allocated.has(row.id) &&
      isDateWithinBillingPeriod(row.date_iso, startDate, endDate),
  );
}

export async function createAllocationsOnSend(invoiceId, lineItems = []) {
  if (!invoiceId) {
    throw new Error("invoiceId is required to create allocations.");
  }

  const activeLines = (lineItems || []).filter(
    (line) => (line.line_status || "active") === "active",
  );

  const usageRows = activeLines
    .filter((line) => line.source_usage_id)
    .map((line) => ({
      invoice_id: invoiceId,
      usage_id: line.source_usage_id,
    }));

  const labourRows = activeLines
    .filter((line) => line.source_time_log_id)
    .map((line) => ({
      invoice_id: invoiceId,
      time_log_id: line.source_time_log_id,
    }));

  if (usageRows.length) {
    const { error } = await supabase
      .from("invoice_usage_allocations")
      .insert(usageRows);

    if (error) wrapSupabaseError(error);
  }

  if (labourRows.length) {
    const { error } = await supabase
      .from("invoice_labour_allocations")
      .insert(labourRows);

    if (error) wrapSupabaseError(error);
  }

  return {
    usageCount: usageRows.length,
    labourCount: labourRows.length,
  };
}
